import { Vec3, vec3, add, sub, scale, cross, length, normalize, ZERO } from '../core/math/vec3'
import { CelestialBody } from '../core/world/body'
import { Vessel, partById } from '../core/vessel/vessel'
import { PartCatalog, partDef } from '../core/vessel/catalog'
import { computeMassProperties } from '../core/vessel/massProperties'
import { shapeRadius } from '../core/vessel/extents'
import { thrustDirection, NO_GIMBAL } from '../core/propulsion/engine'
import { aeroForces, centerOfPressure } from '../core/aero/forces'
import { stagesOf, stagePerformance, totalDeltaV, StagePerformance } from '../game/staging'
import { Design } from './design'

export interface Avviso {
  readonly gravita: 'errore' | 'attenzione' | 'nota'
  readonly testo: string
}

export interface Indicatori {
  readonly massa: number
  readonly massaVuoto: number
  readonly com: Vec3
  readonly comVuoto: Vec3
  /** centro di spinta: media pesata sulla spinta delle posizioni dei motori */
  readonly cot: Vec3 | null
  readonly spinta: number
  readonly direzioneSpinta: Vec3 | null
  /** IL NUMERO CHE CONTA: coppia attorno al baricentro a piena manetta, N·m */
  readonly coppiaResidua: number
  /** quanta coppia il gimbal puo opporre, N·m */
  readonly coppiaDisponibile: number
  readonly cop: Vec3 | null
  /** margine di stabilita in calibri; positivo = stabile */
  readonly margineStabilita: number
  readonly stadi: StagePerformance[]
  readonly deltaVTotale: number
  readonly avvisi: Avviso[]
}

/** Diametro di riferimento: il corpo del razzo, non l'apertura delle alette. */
function diametroRiferimento(vessel: Vessel, catalog: PartCatalog): number {
  let r = 0.5
  for (const p of vessel.parts) {
    const def = partDef(catalog, p.defId)
    if (def.category === 'aero') continue
    r = Math.max(r, shapeRadius(def.shape))
  }
  return 2 * r
}

/** Condizione di riferimento per il centro di pressione: 5 gradi d'incidenza. */
const INCIDENZA_RIFERIMENTO = (5 * Math.PI) / 180

export function calcolaIndicatori(
  design: Design, catalog: PartCatalog, body: CelestialBody,
): Indicatori {
  const vessel = design.vessel
  const avvisi: Avviso[] = []

  if (vessel.parts.length === 0) {
    return {
      massa: 0, massaVuoto: 0, com: ZERO, comVuoto: ZERO, cot: null, spinta: 0,
      direzioneSpinta: null, coppiaResidua: 0, coppiaDisponibile: 0, cop: null,
      margineStabilita: 0, stadi: [], deltaVTotale: 0,
      avvisi: [{ gravita: 'nota', testo: 'Il progetto è vuoto: comincia da un serbatoio.' }],
    }
  }

  const mp = computeMassProperties(vessel, catalog)
  const mpVuoto = computeMassProperties(vessel, catalog, { emptyTanks: true })

  // ── spinta e coppia residua, allo stadio che accende per primo
  const primoStadio = stagesOf(vessel, catalog).find((s) => s.engines.length > 0)
  let spintaTotale = 0
  let forzaTotale: Vec3 = ZERO
  let coppia: Vec3 = ZERO
  let coppiaDisponibile = 0
  let cotPesato: Vec3 = ZERO

  for (const id of primoStadio?.engines ?? []) {
    const parte = partById(vessel, id)
    const def = partDef(catalog, parte.defId).engine!
    const dir = thrustDirection(parte, def, NO_GIMBAL)
    const f = scale(dir, def.thrustSl)
    const braccio = sub(parte.position, mp.com)

    spintaTotale += def.thrustSl
    forzaTotale = add(forzaTotale, f)
    coppia = add(coppia, cross(braccio, f))
    coppiaDisponibile += def.thrustSl * length(braccio) * Math.sin(def.gimbalRange)
    cotPesato = add(cotPesato, scale(parte.position, def.thrustSl))
  }

  const cot = spintaTotale > 0 ? scale(cotPesato, 1 / spintaTotale) : null
  const coppiaResidua = length(coppia)

  // ── centro di pressione, a un'incidenza di riferimento
  const flusso = scale(normalize(vec3(-Math.sin(INCIDENZA_RIFERIMENTO), -Math.cos(INCIDENZA_RIFERIMENTO), 0)), 200)
  const forzeAero = aeroForces(vessel, catalog, flusso, 1)
  const cop = centerOfPressure(forzeAero)
  const diametro = diametroRiferimento(vessel, catalog)
  const margineStabilita = cop ? (mp.com.y - cop.y) / diametro : 0

  const stadi = stagePerformance(vessel, catalog)
  const deltaVTotale = totalDeltaV(vessel, catalog)

  // ── gli avvisi: la voce dell'editor, tutti calcolati, nessuno inventato
  const haMotori = vessel.parts.some((p) => partDef(catalog, p.defId).engine)
  const haComando = vessel.parts.some((p) => partDef(catalog, p.defId).category === 'command')

  if (!haMotori) avvisi.push({ gravita: 'errore', testo: 'Nessun motore montato.' })
  if (!haComando) avvisi.push({ gravita: 'errore', testo: 'Nessuna capsula: non c’è nessuno a pilotare.' })

  // Due situazioni diverse, e vale la pena distinguerle: una spinta un po'
  // disassata il gimbal la corregge (e dovra farlo per tutto il volo); una
  // molto disassata non la corregge nessuno, e il razzo si rovescia.
  if (haMotori && coppiaResidua > coppiaDisponibile) {
    avvisi.push({
      gravita: 'errore',
      testo: 'Il razzo capotta: la spinta è così disassata che il gimbal non basta a raddrizzarla.',
    })
  } else if (haMotori && coppiaResidua > coppiaDisponibile * 0.02) {
    const quota = Math.round((coppiaResidua / coppiaDisponibile) * 100)
    avvisi.push({
      gravita: 'attenzione',
      testo: `Spinta disassata: impegna il ${quota}% dell’autorità del gimbal, e senza correzione il razzo si inclina.`,
    })
  }

  if (cop && margineStabilita < 0) {
    avvisi.push({
      gravita: 'attenzione',
      testo: `Instabile in atmosfera (${margineStabilita.toFixed(1)} calibri): va pilotato col gimbal, come i lanciatori veri.`,
    })
  } else if (cop && margineStabilita > 4) {
    avvisi.push({
      gravita: 'nota',
      testo: `Molto stabile (${margineStabilita.toFixed(1)} calibri): farà fatica a inclinarsi per il giro di gravità.`,
    })
  }

  const primo = stadi.find((s) => s.thrustSeaLevel > 0)
  if (primo && primo.twrSeaLevel < 1.05) {
    avvisi.push({
      gravita: 'errore',
      testo: `Non decolla: rapporto spinta/peso ${primo.twrSeaLevel.toFixed(2)}, serve almeno 1.2.`,
    })
  }

  if (haMotori && deltaVTotale < 4500) {
    avvisi.push({
      gravita: 'attenzione',
      testo: `Δv insufficiente per l’orbita: ${Math.round(deltaVTotale)} m/s, ne servono circa 4500.`,
    })
  }

  return {
    massa: mp.mass,
    massaVuoto: mpVuoto.mass,
    com: mp.com,
    comVuoto: mpVuoto.com,
    cot,
    spinta: spintaTotale,
    direzioneSpinta: spintaTotale > 0 ? normalize(forzaTotale) : null,
    coppiaResidua,
    coppiaDisponibile,
    cop,
    margineStabilita,
    stadi,
    deltaVTotale,
    avvisi,
  }
}
